import { useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Renders a model answer as markdown.
 *
 * SECURITY: raw HTML is never enabled. react-markdown escapes HTML by default and that default
 * must stay — model output is untrusted, and with the tool loop it can carry text straight from
 * an MCP server, so rehype-raw here would be a stored-XSS path into the portal. Links are
 * rendered but forced through rel="noreferrer" and target="_blank".
 */
export function MarkdownAnswer({ text, streaming }: { text: string; streaming?: boolean }) {
  const throttled = useThrottled(text, streaming ? 120 : 0);

  return (
    <div className="md">
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: props => <a {...props} target="_blank" rel="noreferrer noopener" />,
          pre: props => <CodeBlock {...props} />,
          table: props => (
            // Wide tables scroll inside their own box rather than stretching the transcript.
            <div className="md-table-scroll"><table {...props} /></div>
          )
        }}>
        {throttled}
      </Markdown>
    </div>
  );
}

/**
 * Re-parsing a growing string on every SSE delta is wasteful and makes half-written syntax
 * flicker — an unclosed fence renders as a broken block until the next token lands. Coalescing
 * to a fixed interval keeps the stream feeling live without re-parsing dozens of times a second.
 * A delay of 0 (finished turns) passes the value straight through.
 */
function useThrottled(value: string, delayMs: number): string {
  const [shown, setShown] = useState(value);
  const last = useRef(0);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (delayMs <= 0) {
      setShown(value);
      return;
    }

    const since = Date.now() - last.current;
    if (since >= delayMs) {
      last.current = Date.now();
      setShown(value);
      return;
    }

    // Always schedule a trailing update, so the final tokens of a stream are never dropped.
    timer.current = window.setTimeout(() => {
      last.current = Date.now();
      setShown(value);
    }, delayMs - since);

    return () => window.clearTimeout(timer.current);
  }, [value, delayMs]);

  return delayMs <= 0 ? value : shown;
}

/** Fenced code with a copy button — the common case here is SQL worth lifting out whole. */
function CodeBlock(props: React.HTMLAttributes<HTMLPreElement>) {
  const ref = useRef<HTMLPreElement | null>(null);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    const text = ref.current?.innerText ?? '';
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="md-code">
      <button type="button" className="md-copy" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
      <pre {...props} ref={ref} />
    </div>
  );
}
